import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { LoadingSpinner } from '../components/LoadingSpinner'
import { ArrowLeft, Search, Download, Users, Star } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { api } from '../lib/api_new'
import toast from 'react-hot-toast'
import Footer from '../components/Footer'

interface Customer {
  id: string
  name: string
  email: string
  phone?: string
  code: string
  stamps: number
  createdAt: Date
  optInMarketing: boolean
  recentActivity: Array<{
    id: string
    action: 'stamp' | 'redeem'
    timestamp: Date
    staffName: string
  }>
}

export default function AdminDashboard() {
  const [customers, setCustomers] = useState<Customer[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterStamps, setFilterStamps] = useState('all')
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)

  const { staff } = useAuth()
  const navigate = useNavigate()

  useEffect(() => {
    fetchCustomers()
  }, [currentPage, filterStamps])

  const fetchCustomers = async () => {
    try {
      setLoading(true)
      
      const data = await api.getCustomers({
        page: currentPage,
        limit: 20,
        search: searchTerm,
        ...(filterStamps !== 'all' && {
          minStamps: parseInt(filterStamps),
          maxStamps: parseInt(filterStamps)
        })
      })

      setCustomers(data.customers)
      setTotalPages(data.pagination.totalPages)
    } catch (error) {
      console.error('Error fetching customers:', error)
      toast.error('Failed to load customers')
    } finally {
      setLoading(false)
    }
  }

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setCurrentPage(1)
    fetchCustomers()
  }

  const exportCustomers = async () => {
    try {
      // Mock CSV export
      const csvData = customers.map(customer => ({
        Name: customer.name,
        Email: customer.email,
        Phone: customer.phone || '',
        Code: customer.code,
        Stamps: customer.stamps,
        'Created At': new Date(customer.createdAt).toLocaleDateString(),
        'Marketing Opt-in': customer.optInMarketing ? 'Yes' : 'No'
      }))
      
      const csvContent = [
        Object.keys(csvData[0]).join(','),
        ...csvData.map(row => Object.values(row).join(','))
      ].join('\n')
      
      const blob = new Blob([csvContent], { type: 'text/csv' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `customers-${new Date().toISOString().split('T')[0]}.csv`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
      
      toast.success('Customer data exported')
    } catch (error) {
      toast.error('Failed to export data')
    }
  }

  if (!staff || staff.role !== 'admin') {
    return <div>Access denied</div>
  }

  const readyToRedeemCount = customers.filter(c => c.stamps >= 6).length
  const marketingOptInCount = customers.filter(c => c.optInMarketing).length
  const marketingOptInRate = customers.length > 0 ? Math.round((marketingOptInCount / customers.length) * 100) : 0

  return (
    <div className="min-h-screen bg-dark-50">
      {/* Header */}
      <header className="bg-dark-100 shadow-sm border-b border-dark-200">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button
                onClick={() => navigate('/staff/dashboard')}
                className="p-2 text-vapour-600 hover:text-vapour-900 mr-2"
                aria-label="Back to staff dashboard"
              >
                <ArrowLeft size={20} />
              </button>
              <h1 className="text-xl font-bold text-vapour-900">
                Admin Dashboard
              </h1>
            </div>
            
            <button
              onClick={exportCustomers}
              className="bg-dark-200 hover:bg-dark-300 text-vapour-900 font-medium py-2 px-4 rounded-lg transition-colors duration-200 flex items-center"
            >
              <Download size={16} className="mr-2" />
              Export CSV
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-4">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-dark-100 rounded-xl shadow-sm border border-dark-200 p-6">
            <div className="flex items-center">
              <Users className="text-vapour-600 mr-3" size={24} />
              <div>
                <p className="text-sm text-vapour-600">Total Customers</p>
                <p className="text-2xl font-bold text-vapour-900">
                  {customers.length}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-dark-100 rounded-xl shadow-sm border border-dark-200 p-6">
            <div className="flex items-center">
              <Star className="text-yellow-500 mr-3" size={24} />
              <div>
                <p className="text-sm text-vapour-600">Ready to Redeem</p>
                <p className="text-2xl font-bold text-vapour-900">
                  {readyToRedeemCount}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-dark-100 rounded-xl shadow-sm border border-dark-200 p-6">
            <div className="flex items-center">
              <div className="w-6 h-6 bg-green-100 text-green-600 rounded-full flex items-center justify-center mr-3">
                %
              </div>
              <div>
                <p className="text-sm text-vapour-600">Marketing Opt-in</p>
                <p className="text-2xl font-bold text-vapour-900">
                  {marketingOptInRate}%
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-dark-100 rounded-xl shadow-sm border border-dark-200 p-6 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <form onSubmit={handleSearch} className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-vapour-500" size={20} />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by name, email, or code..."
                  className="w-full px-3 py-2 pl-10 bg-dark-200 border border-dark-300 rounded-md text-vapour-900 placeholder-vapour-500 focus:outline-none focus:ring-2 focus:ring-vapour-600 focus:border-transparent"
                />
              </div>
            </form>
            
            <select
              value={filterStamps}
              onChange={(e) => setFilterStamps(e.target.value)}
              className="w-full md:w-48 px-3 py-2 bg-dark-200 border border-dark-300 rounded-md text-vapour-900 focus:outline-none focus:ring-2 focus:ring-vapour-600 focus:border-transparent"
            >
              <option value="all">All Customers</option>
              <option value="0">0 Stamps</option>
              <option value="1">1 Stamp</option>
              <option value="2">2 Stamps</option>
              <option value="3">3 Stamps</option>
              <option value="4">4 Stamps</option>
              <option value="5">5 Stamps</option>
              <option value="6">6 Stamps (Ready)</option>
            </select>
          </div>
        </div>

        {/* Customer List */}
        <div className="bg-dark-100 rounded-xl shadow-sm border border-dark-200 p-6">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <LoadingSpinner size="lg" />
            </div>
          ) : customers.length === 0 ? (
            <div className="text-center py-8 text-vapour-500">
              No customers found
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-dark-200">
                    <th className="text-left py-3 px-2 font-medium text-vapour-700">Customer</th>
                    <th className="text-left py-3 px-2 font-medium text-vapour-700">Code</th>
                    <th className="text-left py-3 px-2 font-medium text-vapour-700">Stamps</th>
                    <th className="text-left py-3 px-2 font-medium text-vapour-700">Joined</th>
                    <th className="text-left py-3 px-2 font-medium text-vapour-700">Recent Activity</th>
                  </tr>
                </thead>
                <tbody>
                  {customers.map((customer) => (
                    <tr key={customer.id} className="border-b border-dark-200 hover:bg-dark-200">
                      <td className="py-3 px-2">
                        <div>
                          <div className="font-medium text-vapour-900">{customer.name}</div>
                          <div className="text-sm text-vapour-600">{customer.email}</div>
                          {customer.phone && (
                            <div className="text-sm text-vapour-500">{customer.phone}</div>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-2">
                        <span className="font-mono text-lg font-bold text-vapour-600">
                          {customer.code}
                        </span>
                      </td>
                      <td className="py-3 px-2">
                        <div className="flex items-center">
                          <span className={`text-lg font-bold ${
                            customer.stamps >= 6 ? 'text-green-400' : 'text-vapour-900'
                          }`}>
                            {customer.stamps}/6
                          </span>
                          {customer.stamps >= 6 && (
                            <span className="ml-2 px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                              Ready
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-2 text-sm text-vapour-600">
                        {new Date(customer.createdAt).toLocaleDateString()}
                      </td>
                      <td className="py-3 px-2">
                        {customer.recentActivity.length > 0 ? (
                          <div className="text-sm">
                            {customer.recentActivity.slice(0, 2).map((activity, idx) => (
                              <div key={idx} className="text-vapour-600">
                                {activity.action === 'stamp' ? '+ Stamp' : '🎁 Redeemed'} by {activity.staffName}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <span className="text-sm text-vapour-400">No activity</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-6 pt-4 border-t border-dark-200">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="bg-dark-200 hover:bg-dark-300 text-vapour-900 font-medium py-2 px-4 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              
              <span className="text-sm text-vapour-600">
                Page {currentPage} of {totalPages}
              </span>
              
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="bg-dark-200 hover:bg-dark-300 text-vapour-900 font-medium py-2 px-4 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          )}
        </div>

        {/* Demo Info */}
        <div className="bg-vapour-100 border border-vapour-200 rounded-xl shadow-sm p-6 mt-6">
          <h3 className="text-lg font-semibold text-vapour-900 mb-2">
            📊 Admin Dashboard
          </h3>
          <p className="text-sm text-vapour-700 mb-3">
            This admin dashboard shows all customer data stored locally in your browser. 
            You can search, filter, and export customer information.
          </p>
          <div className="text-xs text-vapour-600">
            <strong>Features working:</strong> Customer search, stamp filtering, CSV export, real-time updates from staff scanning
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  )
}